import {
  HOSPITAL_LEVELS,
  HOSPITAL_MAX_LEVEL,
  hospitalLevelDefinition,
} from "@ninja/domain";
import { renderNinjaOutfitPortrait as portrait } from "./NinjaOutfitPortrait.js";
import { escapeHtml } from "./escapeHtml.js";
import { formatDuration } from "./formatDuration.js";
import { SectionTabs } from "./SectionTabs.js";
import "./hospital.css";

type ApiRequest = <T>(path: string, init?: RequestInit) => Promise<T>;

interface HospitalCaseView {
  case_id: string;
  ninja_id: string;
  ninja_name: string;
  appearance_id: string;
  status: "waiting" | "treating" | "recovered";
  level_at_fatality: number;
  experience_lost: number;
  is_legacy: boolean;
  fatality_at: string;
  admitted_at: string | null;
  ready_at: string | null;
  recovered_at: string | null;
  recovery_duration_seconds: number | null;
}

interface HospitalLevelDefinition {
  level: number;
  beds: number;
  treatmentTimeMultiplier: number;
}

interface HospitalView {
  hospital: {
    level: number;
    maxLevel?: number;
    beds?: number;
    treatmentTimeMultiplier?: number;
    occupied: number;
    availableBeds: number;
    levelDefinitions?: HospitalLevelDefinition[];
  };
  serverTime: string;
  cases: HospitalCaseView[];
  recentRecoveries: HospitalCaseView[];
}

interface HospitalPageDependencies {
  request: ApiRequest;
  onOpenRoster: () => void;
}

type HospitalPanel = "care" | "effects" | "history";

export class HospitalPage {
  private readonly listenerController = new AbortController();
  private view: HospitalView | null = null;
  private sectionTabs: SectionTabs | null = null;
  private countdownTimer = 0;
  private refreshTimer = 0;
  private serverOffsetMs = 0;
  private destroyed = false;
  private refreshing = false;

  constructor(
    private readonly content: HTMLElement,
    private readonly dependencies: HospitalPageDependencies,
  ) {}

  async mount(): Promise<void> {
    this.content.innerHTML =
      '<div class="hospital-loading" role="status">Loading Hospital…</div>';
    this.content.addEventListener("click", this.handleClick, {
      signal: this.listenerController.signal,
    });

    await this.refresh();
    if (this.destroyed || !this.content.isConnected) return;
    this.countdownTimer = window.setInterval(
      () => this.updateCountdowns(),
      1_000,
    );
    this.refreshTimer = window.setInterval(() => void this.refresh(), 10_000);
  }

  destroy(): void {
    this.destroyed = true;
    window.clearInterval(this.countdownTimer);
    window.clearInterval(this.refreshTimer);
    this.sectionTabs?.destroy();
    this.listenerController.abort();
  }

  private async refresh(): Promise<void> {
    if (this.destroyed || !this.content.isConnected || this.refreshing) return;
    this.refreshing = true;

    try {
      const view = await this.dependencies.request<HospitalView>("/hospital");
      if (this.destroyed || !this.content.isConnected) return;
      this.view = view;
      const serverTime = Date.parse(view.serverTime);
      this.serverOffsetMs = Number.isFinite(serverTime)
        ? serverTime - Date.now()
        : 0;
      this.render();
      this.updateCountdowns();
    } catch (error) {
      if (this.destroyed || !this.content.isConnected) return;
      this.renderError(error);
    } finally {
      this.refreshing = false;
    }
  }

  private render(): void {
    const view = this.view;
    if (!view) return;

    if (!this.content.querySelector("[data-hospital-page]")) {
      this.content.innerHTML = this.renderPageShell();
      this.sectionTabs = new SectionTabs(this.content);
    }

    this.renderOverview();
    this.renderCarePanel();
    this.renderEffectsPanel();
    this.renderHistoryPanel();
    this.sectionTabs?.sync();

    const refreshError = this.content.querySelector<HTMLElement>(
      "[data-hospital-refresh-error]",
    );
    if (refreshError) {
      refreshError.hidden = true;
      refreshError.textContent = "";
    }
  }

  private renderPageShell(): string {
    return `<div class="hospital-page" data-hospital-page>
      <div class="page-heading hospital-page-heading"><div><p class="eyebrow">VILLAGE CARE</p><h1>Hospital</h1></div></div>
      <div class="hospital-overview-strip" data-hospital-overview role="group" aria-label="Hospital summary"></div>
      <nav class="section-tabs" role="tablist" aria-label="Hospital sections">
        <button id="hospital-tab-care" class="section-tab" type="button" role="tab" aria-selected="true" aria-controls="hospital-panel-care" tabindex="0" data-section-tab="care">Care and recovery</button>
        <button id="hospital-tab-effects" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="hospital-panel-effects" tabindex="-1" data-section-tab="effects">Hospital effects</button>
        <button id="hospital-tab-history" class="section-tab" type="button" role="tab" aria-selected="false" aria-controls="hospital-panel-history" tabindex="-1" data-section-tab="history">Recently recovered</button>
      </nav>
      <section id="hospital-panel-care" role="tabpanel" tabindex="0" aria-labelledby="hospital-tab-care" data-section-panel="care">
        <div data-hospital-panel-content="care"></div>
      </section>
      <section id="hospital-panel-effects" role="tabpanel" tabindex="0" aria-labelledby="hospital-tab-effects" data-section-panel="effects" hidden>
        <div data-hospital-panel-content="effects"></div>
      </section>
      <section id="hospital-panel-history" role="tabpanel" tabindex="0" aria-labelledby="hospital-tab-history" data-section-panel="history" hidden>
        <div data-hospital-panel-content="history"></div>
      </section>
      <p class="hospital-refresh-error" data-hospital-refresh-error role="status" hidden></p>
    </div>`;
  }

  private renderOverview(): void {
    const view = this.view;
    const overview = this.content.querySelector<HTMLElement>(
      "[data-hospital-overview]",
    );
    if (!view || !overview) return;

    const currentDefinition = hospitalLevelDefinition(view.hospital.level);
    const beds = view.hospital.beds ?? currentDefinition.beds;
    const maxLevel = view.hospital.maxLevel ?? HOSPITAL_MAX_LEVEL;
    const treatmentTimeMultiplier =
      view.hospital.treatmentTimeMultiplier ??
      currentDefinition.treatmentTimeMultiplier;

    overview.innerHTML = `<div class="hospital-overview-metric"><span>AVAILABLE BEDS</span><strong>${view.hospital.availableBeds}/${beds}</strong></div>
      <div class="hospital-overview-metric"><span>HOSPITAL LEVEL</span><strong>${view.hospital.level}/${maxLevel}</strong></div>
      <div class="hospital-overview-metric"><span>BASE TREATMENT TIME</span><strong>${Math.round(treatmentTimeMultiplier * 100)}%</strong></div>`;
  }

  private renderCarePanel(): void {
    const view = this.view;
    const panel = this.panelContent("care");
    if (!view || !panel) return;

    const treating = view.cases.filter((entry) => entry.status === "treating");
    const waiting = view.cases.filter((entry) => entry.status === "waiting");
    const cases = [...treating, ...waiting];
    panel.innerHTML = `<section class="hospital-panel">
      <div class="section-heading"><div><p class="eyebrow">CURRENT CASES · ${cases.length}</p><h2>Care and recovery</h2></div><button id="hospital-roster" class="text-button" type="button">Open roster <span aria-hidden="true">→</span></button></div>
      ${cases.length ? `<div class="hospital-case-list">${cases.map((entry) => this.renderCase(entry)).join("")}</div>` : '<div class="empty-state">No ninjas need hospital care. Your roster is ready for the field.</div>'}
    </section>`;
  }

  private renderEffectsPanel(): void {
    const view = this.view;
    const panel = this.panelContent("effects");
    if (!view || !panel) return;

    const definitions =
      view.hospital.levelDefinitions?.length
        ? view.hospital.levelDefinitions
        : HOSPITAL_LEVELS;
    const rows = definitions
      .map((definition) => {
        const current = definition.level === view.hospital.level;
        const currentLabel = current
          ? '<span class="building-level-current-tag">CURRENT</span>'
          : "";
        return `<tr class="${current ? "current-level" : ""}"><td>Lv. ${definition.level} ${currentLabel}</td><td>${definition.beds}</td><td>${Math.round(definition.treatmentTimeMultiplier * 100)}%</td></tr>`;
      })
      .join("");

    panel.innerHTML = `<section class="hospital-panel">
      <div class="section-heading"><div><p class="eyebrow">LEVEL CONFIGURATION · CURRENT LEVEL ${view.hospital.level}</p><h2>Hospital effects</h2></div></div>
      <p class="hospital-effects-note">Lower treatment time means faster recovery.</p>
      <div class="building-level-table-wrap"><table class="building-level-table"><thead><tr><th>LEVEL</th><th>BEDS</th><th>BASE TREATMENT TIME</th></tr></thead><tbody>${rows}</tbody></table></div>
    </section>`;
  }

  private renderHistoryPanel(): void {
    const view = this.view;
    const panel = this.panelContent("history");
    if (!view || !panel) return;

    const recoveries = Array.isArray(view.recentRecoveries)
      ? view.recentRecoveries
      : [];
    const recoveryEntries = recoveries
      .map((entry) => {
        const recoveredAt = entry.recovered_at
          ? new Date(entry.recovered_at).toLocaleString()
          : "";
        return `<article><span class="recovery-mark" aria-hidden="true">✓</span><div><b>${escapeHtml(entry.ninja_name)}</b><small>Recovered · HP restored</small></div><time>${escapeHtml(recoveredAt)}</time></article>`;
      })
      .join("");

    panel.innerHTML = `<section class="hospital-panel">
      <div class="section-heading"><div><p class="eyebrow">LAST 24 HOURS</p><h2>Recently recovered</h2></div><span class="muted">${recoveries.length} ${recoveries.length === 1 ? "recovery" : "recoveries"}</span></div>
      ${recoveries.length ? `<div class="hospital-recoveries">${recoveryEntries}</div>` : '<div class="empty-state">No ninjas have recovered in the last 24 hours.</div>'}
    </section>`;
  }

  private renderCase(entry: HospitalCaseView): string {
    const treating = entry.status === "treating";
    const status = treating ? "IN TREATMENT" : "WAITING FOR A BED";
    const timer =
      treating && entry.ready_at
        ? `<span class="hospital-timer" data-hospital-ready="${escapeHtml(entry.ready_at)}">Calculating…</span>`
        : '<span class="hospital-timer">Treatment begins when a bed opens.</span>';
    const penalty = entry.is_legacy
      ? "Legacy case · no XP penalty was applied"
      : `${entry.experience_lost} XP lost at incapacitation`;
    return `<article class="hospital-case"><div class="hospital-case-portrait">${portrait(entry.appearance_id, entry.ninja_name)}</div><div class="hospital-case-info"><div class="hospital-case-meta"><span class="hospital-status ${treating ? "treating" : "waiting"}">${status}</span><span>LEVEL ${entry.level_at_fatality}</span></div><h3>${escapeHtml(entry.ninja_name)}</h3><p>${penalty}</p>${timer}</div></article>`;
  }

  private panelContent(panel: HospitalPanel): HTMLElement | null {
    return this.content.querySelector<HTMLElement>(
      `[data-hospital-panel-content="${panel}"]`,
    );
  }

  private updateCountdowns(): void {
    const now = Date.now() + this.serverOffsetMs;
    this.content
      .querySelectorAll<HTMLElement>("[data-hospital-ready]")
      .forEach((element) => {
        const readyAt = Date.parse(element.dataset.hospitalReady ?? "");
        if (!Number.isFinite(readyAt)) {
          element.textContent = "Calculating…";
          return;
        }
        const remaining = Math.max(0, readyAt - now);
        element.textContent =
          remaining <= 0
            ? "Completing treatment…"
            : `Ready in ${formatDuration(remaining)}`;
      });
  }

  private renderError(error: unknown): void {
    const message = error instanceof Error ? error.message : String(error);
    if (!this.content.querySelector("[data-hospital-page]")) {
      this.content.innerHTML = `<div class="page-heading hospital-page-heading"><div><p class="eyebrow">VILLAGE CARE</p><h1>Hospital</h1></div></div><div class="empty-state">${escapeHtml(message)}</div>`;
      return;
    }

    const notice = this.content.querySelector<HTMLElement>(
      "[data-hospital-refresh-error]",
    );
    if (notice) {
      notice.hidden = false;
      notice.textContent = `Could not refresh Hospital data: ${message}`;
    }
  }

  private handleClick = (event: MouseEvent): void => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest<HTMLButtonElement>("#hospital-roster")) {
      this.dependencies.onOpenRoster();
    }
  };
}
